/**
 * @module features/devices/components/DeviceTelemetryDocsPanel
 * @description Embedded, device-specific Telemetry Integration documentation:
 * endpoint, required headers, payload fields, canonical HMAC-SHA256 signing snippet
 * and response-signature verification snippet. Contains no secrets (placeholders only).
 */
import * as React from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TelemetryDocumentation } from "../types/telemetry";

interface DeviceTelemetryDocsPanelProps {
  docs: TelemetryDocumentation;
}

const FIELD_ROWS: Array<[string, string, string]> = [
  ["wifi_strength", "low | medium | high", "Signal quality bucket"],
  ["cpu_load", "number 0–100", "CPU load percentage"],
  ["memory_load_percentage", "number 0–100", "Memory used percentage"],
  ["memory_available", "number (MB) ≥ 0", "Available RAM"],
  ["memory_total", "number (MB) > 0", "Total RAM"],
  ["connected_wifi_name", "string ≤ 64", "Connected SSID"],
  ["wifi_rssi", "string ≤ 32", "e.g. \"-58 dBm\""],
  ["mac_id", "string", "Must equal the registered MAC (else 403)"],
  ["os_hash", "string ≤ 128", "OS build / integrity hash"],
  ["payload_sign", "hex string (64)", "HMAC-SHA256 of the canonical payload"],
];

export const SIGNING_SNIPPET = `import hmac, hashlib, json

def sign(payload: dict, signature_key: str) -> str:
    body = {k: v for k, v in payload.items() if k != "payload_sign"}
    canonical = json.dumps(body, sort_keys=True, separators=(",", ":"))
    return hmac.new(signature_key.encode(), canonical.encode(), hashlib.sha256).hexdigest()

payload["payload_sign"] = sign(payload, DEVICE_SIGNATURE_KEY)`;

export const VERIFY_SNIPPET = `def verify_response(data: dict, signature_key: str) -> bool:
    msg = f"{data['request_id']}:{data['timestamp']}"   # timestamp is ISO-8601 UTC
    expected = hmac.new(signature_key.encode(), msg.encode(), hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, data["response_signature"])`;

function CodeBlock({ code, label }: { code: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable: the block remains selectable */
    }
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg border border-border/80 bg-muted/40 p-3 pr-10 text-[11px] leading-relaxed font-mono text-foreground">
        <code>{code}</code>
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={copy}
        aria-label={`Copy ${label}`}
        className="absolute right-1.5 top-1.5 h-7 w-7"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">{children}</h4>;
}

export function DeviceTelemetryDocsPanel({ docs }: DeviceTelemetryDocsPanelProps) {
  return (
    <div className="space-y-5" data-testid="telemetry-docs">
      <section>
        <Heading>Endpoint</Heading>
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/80 bg-card p-3">
          <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            {docs.method}
          </span>
          <code className="min-w-0 break-all font-mono text-[11px]">{docs.ingestion_endpoint}</code>
        </div>
      </section>

      <section>
        <Heading>Required headers</Heading>
        <dl className="rounded-lg border border-border/80 bg-card divide-y divide-border/60">
          {Object.entries(docs.required_headers).map(([k, v]) => (
            <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-3 py-2">
              <dt className="font-mono text-[11px] font-semibold sm:w-44 shrink-0">{k}</dt>
              <dd className="font-mono text-[11px] text-muted-foreground break-all">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section>
        <Heading>Payload fields</Heading>
        <div className="overflow-x-auto rounded-lg border border-border/80 bg-card">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Field</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {FIELD_ROWS.map(([f, t, n]) => (
                <tr key={f} className="border-t border-border/60">
                  <td className="px-3 py-1.5 font-mono font-semibold">{f}</td>
                  <td className="px-3 py-1.5 font-mono text-muted-foreground">{t}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <Heading>Sign the request (HMAC-SHA256)</Heading>
        <p className="mb-2 text-[11px] text-muted-foreground">{docs.canonical_signing_algorithm}</p>
        <CodeBlock code={SIGNING_SNIPPET} label="signing snippet" />
      </section>

      <section>
        <Heading>Verify the response</Heading>
        <p className="mb-2 text-[11px] text-muted-foreground">{docs.response_verification_algorithm}</p>
        <CodeBlock code={VERIFY_SNIPPET} label="verification snippet" />
      </section>

      <section>
        <Heading>Sample payload</Heading>
        <CodeBlock code={JSON.stringify(docs.sample_payload, null, 2)} label="sample payload" />
      </section>

      <p className="text-[11px] text-muted-foreground">{docs.retention_policy}</p>
    </div>
  );
}

